import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import ModalCloseButton from '../../shared/components/modals/ModalCloseButton';
import StatusNotice from '../../shared/components/common/StatusNotice';
import { DeviceIDBadge, EmployeeIDBadge } from '../../shared/components/common/Badges';
import RoleBadge from '../../shared/components/common/RoleBadge';
import { toBoolean } from '../../utils/booleanUtils';
import './LocationDeleteBlockedModal.css';

const BLOCKED_LIST_LIMIT = 25;

function BlockedListSummary({
  truncated,
  returned,
  total,
  noun,
  nouns,
  onNavigate,
  navigateLabel,
  navigateLabelCompact,
}) {
  if (!total) return null;

  return (
    <p className="location-delete-blocked-list-summary">
      {truncated
        ? `Showing ${returned} of ${total} ${nouns} (most recently updated, max ${BLOCKED_LIST_LIMIT}).`
        : `Showing all ${total} ${total === 1 ? noun : nouns}.`}
      {onNavigate && (
        <>
          {' '}
          <button
            type="button"
            className="location-delete-blocked-view-all-link"
            onClick={onNavigate}
          >
            {truncated ? navigateLabel : navigateLabelCompact}
          </button>
        </>
      )}
    </p>
  );
}

function LocationDeleteBlockedModal({
  blockedInfo,
  onClose,
  onViewAllDevices,
  onViewAllUsers,
}) {
  const [tab, setTab] = useState('devices');

  useEffect(() => {
    if (!blockedInfo) return undefined;

    const deviceCount = Number(blockedInfo.device_count || 0);
    const userCount = Number(blockedInfo.user_count || 0);
    setTab(deviceCount > 0 ? 'devices' : (userCount > 0 ? 'users' : 'devices'));

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow || 'unset';
      document.documentElement.style.overflow = previousHtmlOverflow || 'unset';
    };
  }, [blockedInfo]);

  if (!blockedInfo) return null;

  const deviceCount = Number(blockedInfo.device_count || 0);
  const userCount = Number(blockedInfo.user_count || 0);
  const hasDevices = deviceCount > 0;
  const hasUsers = userCount > 0;
  const locationLabel = blockedInfo.location_name || 'This location';
  const isLocationActive = toBoolean(blockedInfo.is_active);
  const locationId = blockedInfo.location_id;
  const devicesReturned = blockedInfo.devices_returned ?? blockedInfo.devices?.length ?? 0;
  const usersReturned = blockedInfo.users_returned ?? blockedInfo.users?.length ?? 0;
  const devicesTruncated = Boolean(blockedInfo.devices_truncated) || deviceCount > devicesReturned;
  const usersTruncated = Boolean(blockedInfo.users_truncated) || userCount > usersReturned;

  const handleViewAllDevices = () => {
    if (locationId == null || !onViewAllDevices) return;
    onViewAllDevices(locationId);
    onClose();
  };

  const handleViewAllUsers = () => {
    if (locationId == null || !onViewAllUsers) return;
    onViewAllUsers(locationId);
    onClose();
  };

  const content = (
    <div className="location-delete-blocked-overlay" onClick={onClose} role="presentation">
      <div
        className="location-delete-blocked-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="location-delete-blocked-title"
      >
        <div className="location-delete-blocked-header">
          <h2 id="location-delete-blocked-title">Cannot Delete Location</h2>
          <ModalCloseButton onClick={onClose} title="Close" />
        </div>

        <div className="location-delete-blocked-body">
          <StatusNotice
            variant="danger"
            role="alert"
            className="location-delete-blocked-notice"
            title={(
              <>
                <strong>{locationLabel}</strong>
                {' '}
                is still in use and cannot be deleted.
              </>
            )}
          >
            Reassign or remove all linked users and devices, then try again.
          </StatusNotice>

          {isLocationActive && (
            <StatusNotice
              variant="info"
              className="location-delete-blocked-notice location-delete-blocked-notice--info"
              title="Need to stop access instead?"
            >
              Disable this location from the row actions menu. That blocks logins and ends
              active sessions while keeping users, devices, and history. Remove or reassign
              linked records first if you still want to delete.
            </StatusNotice>
          )}

          <div className="device-tabs" role="tablist" aria-label="Linked records">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'devices'}
              className={`device-tab-btn${tab === 'devices' ? ' active' : ''}`}
              onClick={() => setTab('devices')}
            >
              Devices ({deviceCount})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'users'}
              className={`device-tab-btn${tab === 'users' ? ' active' : ''}`}
              onClick={() => setTab('users')}
            >
              Users ({userCount})
            </button>
          </div>

          {tab === 'devices' && (
            <div className="location-delete-blocked-table-section">
              {!hasDevices ? (
                <p className="location-delete-blocked-empty">No devices are assigned to this location.</p>
              ) : (
                <>
                  <BlockedListSummary
                    truncated={devicesTruncated}
                    returned={devicesReturned}
                    total={deviceCount}
                    noun="device"
                    nouns="devices"
                    onNavigate={onViewAllDevices ? handleViewAllDevices : null}
                    navigateLabel="View complete list in Devices"
                    navigateLabelCompact="Open in Devices"
                  />
                  {Array.isArray(blockedInfo.devices) && blockedInfo.devices.length > 0 ? (
                    <div className="location-delete-blocked-table-wrap">
                      <table className="location-delete-blocked-table">
                        <thead>
                          <tr>
                            <th>Device ID</th>
                            <th>Device Name</th>
                            <th>Department</th>
                          </tr>
                        </thead>
                        <tbody>
                          {blockedInfo.devices.map((device) => (
                            <tr key={device.id}>
                              <td>
                                <DeviceIDBadge deviceId={device.device_identifier || '—'} size="small" />
                              </td>
                              <td>{device.device_name || '—'}</td>
                              <td>{device.department || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="location-delete-blocked-empty">No device details were returned.</p>
                  )}
                </>
              )}
            </div>
          )}

          {tab === 'users' && (
            <div className="location-delete-blocked-table-section">
              {!hasUsers ? (
                <p className="location-delete-blocked-empty">No users are assigned to this location.</p>
              ) : (
                <>
                  <BlockedListSummary
                    truncated={usersTruncated}
                    returned={usersReturned}
                    total={userCount}
                    noun="user"
                    nouns="users"
                    onNavigate={onViewAllUsers ? handleViewAllUsers : null}
                    navigateLabel="View complete list in Users"
                    navigateLabelCompact="Open in Users"
                  />
                  {Array.isArray(blockedInfo.users) && blockedInfo.users.length > 0 ? (
                    <div className="location-delete-blocked-table-wrap">
                      <table className="location-delete-blocked-table">
                        <thead>
                          <tr>
                            <th>Employee ID</th>
                            <th>Name</th>
                            <th>Role</th>
                            <th>Email</th>
                          </tr>
                        </thead>
                        <tbody>
                          {blockedInfo.users.map((user) => (
                            <tr key={user.id}>
                              <td>
                                <EmployeeIDBadge employeeId={user.employee_id || '—'} size="small" />
                              </td>
                              <td>{user.full_name || '—'}</td>
                              <td>{user.role ? <RoleBadge role={user.role} size="small" /> : '—'}</td>
                              <td>{user.email || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="location-delete-blocked-empty">No user details were returned.</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="location-delete-blocked-footer">
          <button type="button" className="action-btn action-primary" onClick={onClose}>
            <span className="action-text">Close</span>
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}

export default LocationDeleteBlockedModal;
